import { ModalRef, sendNotification, useTranslation } from '@msdk/common'
import { Entity, GenericFieldValue } from '@msdk/definitions'
import {
  ButtonPrimary,
  COLORS,
  createComponentWrapperWithRef,
  FormWrapper,
  Pagination,
  styled,
  TableWrapper,
} from '@msdk/ui'
import React, { useEffect } from 'react'

import { useFormModal, useModalView } from '../../hooks'
import { DynamicView } from '../DynamicView'
import { DynamicViewProvider } from '../DynamicView/context'
import { Filters } from '../Filters'
import { useViewContext } from '../ViewContextProvider'

const StyledFormWrapper = styled(FormWrapper)`
  padding: 24px 24px 16px;
  border-top: 1px solid ${COLORS.divider};
  border-bottom: 1px solid ${COLORS.divider};
  overflow: hidden;
`

const PaginationWrapper = styled('div')`
  display: flex;
`

const FiltersWrapper = styled('div')`
  width: 100%;
  padding-top: 8px;
  padding-bottom: 24px;
`

const BaseStyledPagination = styled(Pagination)`
  margin-left: auto;
`
const NovaStyledPagination = styled(BaseStyledPagination)`
  border: 0;
`
const StyledPagination = createComponentWrapperWithRef(
  {
    mui: BaseStyledPagination,
    nova: NovaStyledPagination,
  },
  'StyledPagination',
)

interface Props {
  entityName: string
  viewName: string
  modalRef?: ModalRef
  lookup: boolean
  multiSelect?: boolean
  selectedRowIds?: string[]
  prefilledFilters?: Record<string, GenericFieldValue>
}

export function SignleModalView(props: Props) {
  const { createModal: createFormModal } = useFormModal()

  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    onPageChange,
    fetchData,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentFilters,
    isMultiselected,
    setIsMultiselect,
    additionalCondition,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    isInitialized,
    currentOrder,
    addOrUpdateFilter,
  } = useModalView(props.entityName, props.viewName, !!props.multiSelect, props.selectedRowIds, props.prefilledFilters)

  const viewContext = useViewContext()

  const toggleSelection = (row: Entity) => {
    const newSelection = selectedRows.includes(row) ? selectedRows.filter((r) => r !== row) : selectedRows.concat([row])
    onRowSelected(newSelection)
  }

  const onRowClicked = (row: Entity) => {
    if (props.lookup && !isMultiselected) {
      props.modalRef?.close(row)
      return
    }

    if (props.lookup && isMultiselected) {
      toggleSelection(row)
      return
    }

    const formToOpen = viewModel.openFormModel ?? entityModel.defaultForm
    if (formToOpen) {
      const modalRef = createFormModal(
        entityModel.name,
        formToOpen,
        row.id,
        () => {
          fetchData()
        },
        { mode: 'Readonly', modalForm: viewModel.modalForm },
      )
      modalRef.open()
    } else {
      sendNotification({
        message:
          'Missing DefaultForm in entity model and no OpenFormModel was specified in the view, can not determine the form to open!',
        status: 'error',
      })
    }
  }

  const onConfirmSelection = (selected: Entity[]) => {
    props.modalRef?.close(selected)
  }

  useEffect(() => {
    const newCtx = {
      isLoading,
      response,
      acl,
      reload,
      selectedRows,
      currentFilters,
      fetchData,
      isInitialized,
    }
    viewContext.update(newCtx)
  }, [isLoading, response, acl, reload, selectedRows, currentFilters, fetchData, isInitialized])

  const t = useTranslation()

  return (
    <>
      {viewModel && (
        <StyledFormWrapper>
          <FiltersWrapper>
            <Filters
              filters={viewModel.filters}
              viewName={viewModel.name}
              onFilterChange={filterChange}
              prefilledFilters={props.prefilledFilters}
              multiselect={isMultiselected}
              setMultiselect={setIsMultiselect}
              acl={acl}
              entityModel={entityModel}
              hideBinnedItemsFilter={props.lookup}
            />
          </FiltersWrapper>
          <TableWrapper>
            <DynamicViewProvider
              entityModel={entityModel}
              viewModel={viewModel}
              reload={reload}
              rows={response?.source || []}
              isLoading={isLoading}
              onRowClick={onRowClicked}
              onOrderChange={onOrderChange}
              multiSelect={isMultiselected}
              selectedRows={selectedRows}
              onRowSelected={onRowSelected}
              acl={acl}
              modalRef={props.modalRef}
              currentFilters={currentFilters}
              additionalCondition={additionalCondition}
              addColumnFilter={addColumnFilter}
              removeColumnFilter={removeColumnFilter}
              removeAllColumnFilter={removeAllColumnFilter}
              currentOrder={currentOrder}
              addOrUpdateFilter={addOrUpdateFilter}
            >
              <DynamicView />
            </DynamicViewProvider>
          </TableWrapper>
          {!viewModel.hidePagination && (
            <PaginationWrapper>
              <StyledPagination
                count={response?.totalItems ?? 0}
                page={currentPage}
                rowsPerPage={response?.pageSize ?? 30}
                rowsPerPageOptions={[]}
                onPageChange={(_e, page) => onPageChange(page)}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('CMPSR_PAGINATOR_OF', 'of')} ${count}`}
              />
            </PaginationWrapper>
          )}
          {isMultiselected && (
            <ButtonPrimary
              onClick={() => onConfirmSelection(selectedRows)}
              disabled={!selectedRows || selectedRows.length === 0}
            >
              {t('CMPS_VIEW_MULTI_SELECT_CONFIRM', 'Confirm Selection')}
            </ButtonPrimary>
          )}
        </StyledFormWrapper>
      )}
    </>
  )
}
